import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Row, Col } from '../Grid';
import { RSInput, RSFormLabel, RSButton, RSRadio } from 'reactsymbols-kit';
import RadioGroup from '../RadioGroup';
import EditDependencyController from '../../merode/controllers/EditDependencyController';
import Styles from '../../styles/colors';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IDependencyInspector } from '../../stores/ui/dependency_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange } from './rsForm';

interface DependencyInspectorProps {
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** The dependency to inspect. */
  dependency: IDependency;
  /** The DependencyInspector ui store. */
  ui: IDependencyInspector;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to close the inspector. */
  requestClose?: () => void;
}

/**
 * Dependency inspector.
 *
 * Props:
 *
 * - version: The ModelVersion (to pass to the controller)
 * - dependency: the dependency to inspect
 * - ui: the DependencyInspector store
 * - onReport: callback to call when a report is generated.
 * - requestClose: callback to close the inspector.
 *
 * Reset-on-element-swap is handled by key-remount in Inspector/index.js (key on
 * the dependency id), replacing the old getDerivedStateFromProps. There is no
 * local state to reset; the store-side `ui.discardChanges()` the class ran in
 * gDSFP now runs in a cleanup effect (see below). Selecting a different
 * dependency while dirty already discards via ProjectVersion.confirmable before
 * this remounts, so the fresh mount reads a clean `ui.changes`.
 */
const DependencyInspector = observer(function DependencyInspector({
  version,
  dependency,
  ui,
  onReport,
  requestClose,
}: DependencyInspectorProps) {
  // The controller is created once per mount (constructor instance field).
  const controllerRef = useRef<EditDependencyController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new EditDependencyController(version);
  }
  const controller = controllerRef.current;

  // Defensive discard of pending store edits when this inspector unmounts (key
  // remount on dependency swap). Mirrors the ui.discardChanges() the class ran
  // in getDerivedStateFromProps on prop swap.
  const uiRef = useRef(ui);
  uiRef.current = ui;
  useEffect(() => {
    return () => {
      uiRef.current.discardChanges();
    };
  }, []);

  const { changes } = ui;

  /**
   * Utitility helpers to:
   * - get the dependency properties either from the changes or from the
   *   dependency.
   * - check whether a property changed.
   */
  const masterName = changes.masterName !== null ? changes.masterName : dependency.name.master;
  const masterNameChanged = changes.masterName !== null;
  const dependentName =
    changes.dependentName !== null ? changes.dependentName : dependency.name.dependent;
  const dependentNameChanged = changes.dependentName !== null;
  const type = changes.type !== null ? changes.type : dependency.type;

  const discardChanges = () => {
    ui.discardChanges();
  };

  const applyChanges = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editDependency(dependency.id, {
      name: {
        master: masterName,
        dependent: dependentName,
      },
      type,
    });
    if (report.successful) {
      discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  const deleteDependency = async () => {
    ui.setProjectBusy(true);
    const report = await controller.deleteDependency(dependency.id);
    if (requestClose) requestClose();
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  const renderButton = () => {
    if (ui.dirty) {
      return (
        <div className="editDependency p-[15px]">
          <Row gutter={5}>
            <Col md={6}>
              <RSButton
                background={Styles.defaultGradient}
                color={Styles.darkTextColor}
                value="Discard"
                onClick={discardChanges}
              />
            </Col>
            <Col md={6}>
              <RSButton
                background={Styles.blueGradient}
                value="Apply changes"
                onClick={applyChanges}
              />
            </Col>
          </Row>
        </div>
      );
    } else {
      return (
        <div className="removeDependency p-[15px]">
          <RSButton
            background={Styles.redGradient}
            value="Delete dependency"
            onClick={deleteDependency}
          />
        </div>
      );
    }
  };

  const masterCheck = controller.checkNameInput(masterName);
  const dependentCheck = controller.checkNameInput(dependentName);
  return (
    <div className="dependency-inspector flex h-full flex-col">
      <div className="dependency-content flex-1 overflow-auto px-[15px] py-5">
        <RSFormLabel>Dependency name</RSFormLabel>

        <Row gutter={30}>
          <Col md={6}>
            <RSInput
              label="Role of master"
              value={masterName}
              onChange={asRSChange<RSInputChange>((value: string) =>
                changes.storeChange('masterName', dependency.name.master, value)
              )}
              className={masterNameChanged ? 'dirty' : ''}
              level={rsValue(masterCheck.ok ? null : 'danger')}
              text={rsValue(masterCheck.ok ? null : masterCheck.message)}
            />
          </Col>
          <Col md={6} className="dependentNamePart">
            <RSInput
              label="Role of dependent"
              value={dependentName}
              onChange={asRSChange<RSInputChange>((value: string) =>
                changes.storeChange('dependentName', dependency.name.dependent, value)
              )}
              className={dependentNameChanged ? 'dirty' : ''}
              level={rsValue(dependentCheck.ok ? null : 'danger')}
              text={rsValue(dependentCheck.ok ? null : dependentCheck.message)}
            />
          </Col>
        </Row>
        <RSFormLabel>Master object</RSFormLabel>
        {dependency.master.name}

        <RSFormLabel>Dependent object</RSFormLabel>
        {dependency.dependent.name}

        <RSFormLabel>Dependency type</RSFormLabel>
        <RadioGroup
          key={
            dependency.id /* Work-around for bug in RSRadioGroup: make sure it is swapped by a new component when the dependency changes */
          }
          selectedValue={type}
          name="radio-group"
          onChange={(value) => changes.storeChange('type', dependency.type, value)}
        >
          <RSRadio value="OPTIONAL_N" label="Optional N" />
          <RSRadio value="OPTIONAL_1" label="Optional 1" />
          <RSRadio value="MANDATORY_N" label="Mandatory N" />
          <RSRadio value="MANDATORY_1" label="Mandatory 1" />
        </RadioGroup>
      </div>
      {renderButton()}
    </div>
  );
});

export default DependencyInspector;
